<section
  class="flex h-full w-full flex-col overflow-hidden bg-gray-50 text-gray-900"
  role="dialog"
  [attr.aria-label]="'profile' | translate"
>
  <header class="flex shrink-0 flex-col border-b border-gray-100 bg-white px-5 pt-4">
    <div class="flex items-center gap-3">
      <h2 class="m-0 min-w-0 flex-1 truncate text-xl font-bold leading-7 text-gray-900">
        {{ employee ? fullName : ('profile' | translate) }}
      </h2>
      <button
        type="button"
        class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"
        [attr.aria-label]="'close' | translate"
        (click)="onClose()"
      >
        <app-icon>close</app-icon>
      </button>
    </div>
    <app-tabs [tabList]="tabs" (onSelectTab)="onSelectTab($event)" />
  </header>

  @if (isLoading) {
    <div class="flex flex-1 items-center justify-center">
      <app-loader />
    </div>
  } @else if (employee && activeTab === EmployeeProfileTab.GENERAL) {
    <div class="crm-scroll min-h-0 flex-1 overflow-y-auto p-5">
      <div class="grid grid-cols-1 items-stretch gap-4 md:grid-cols-[300px_minmax(0,1fr)]">
        <!-- Photo -->
        <div class="flex flex-col items-center justify-center gap-4 rounded-2xl border border-gray-200 bg-white p-5">
          <app-avatar size="176px" [url]="avatarUrl" [title]="fullName" [status]="employee.status" [isPhotoGallery]="!avatarPreviewUrl" />
          @if (isUploadingAvatar) {
            <div class="absolute inset-0 flex items-center justify-center rounded-full bg-white/60">
              <app-loader size="small" />
            </div>
          }

          @if (payload.canEdit) {
            <button
              type="button"
              class="!inline-flex h-9 items-center gap-2 rounded-full border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-600 disabled:pointer-events-none disabled:opacity-60"
              [disabled]="isUploadingAvatar"
              (click)="openAvatarUpload()"
            >
              <app-icon>upload</app-icon>
              <span class="whitespace-nowrap">{{
                (employee.avatar_url ? 'employee_profile_change_photo' : 'employee_profile_upload_photo') | translate
              }}</span>
            </button>
          }
        </div>

        <!-- General -->
        <div class="rounded-2xl border border-gray-200 bg-white pt-4 pl-5 pr-5 pb-5">
          <div class="flex items-center justify-between gap-3 border-b border-gray-100 pb-3">
            <h3 class="m-0 text-lg font-semibold text-gray-800">{{ 'general' | translate }}</h3>
            @if (payload.canEdit && !isEditing) {
              <button
                type="button"
                class="flex items-center gap-1 rounded-lg px-2 py-1 text-sm text-gray-500 hover:bg-gray-100 hover:text-blue-600"
                (click)="startEditing()"
              >
                <app-icon class="!h-4 !w-4 !text-[16px] !leading-4">edit</app-icon>
                {{ 'edit' | translate }}
              </button>
            }
          </div>

          @if (isEditing) {
            <div class="flex flex-col gap-4 pt-4">
              <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div class="flex flex-col gap-1">
                  <span class="text-xs text-gray-500">{{ 'first_name' | translate }} <span class="text-red-500">*</span></span>
                  <app-input
                    [formControl]="form.controls.first_name"
                    [placeholder]="'first_name' | translate"
                    [invalid]="isInvalid('first_name')"
                    [showErrors]="false"
                  />
                </div>
                <div class="flex flex-col gap-1">
                  <span class="text-xs text-gray-500">{{ 'last_name' | translate }} <span class="text-red-500">*</span></span>
                  <app-input
                    [formControl]="form.controls.last_name"
                    [placeholder]="'last_name' | translate"
                    [invalid]="isInvalid('last_name')"
                    [showErrors]="false"
                  />
                </div>
              </div>

              <div class="flex flex-col gap-1">
                <span class="text-xs text-gray-500">{{ 'role' | translate }} <span class="text-red-500">*</span></span>
                <app-select
                  [formControl]="form.controls.group_id"
                  [options]="groups"
                  optionValue="id"
                  optionLabel="name"
                  [placeholder]="'choose_role' | translate"
                  [error]="isInvalid('group_id')"
                  [showErrors]="false"
                />
              </div>

              <div class="flex justify-end gap-2">
                <app-button btnType="stroked" [disabled]="isSaving" (onChangeClick)="cancelEditing()">
                  {{ 'cancel' | translate }}
                </app-button>
                <app-button [disabled]="isSaving" (onChangeClick)="save()">
                  @if (isSaving) {
                    <app-loader size="tiny" strokeWidth="3" />
                  } @else {
                    {{ 'save' | translate }}
                  }
                </app-button>
              </div>
            </div>
          } @else {
            <dl class="m-0 flex flex-col gap-4 pt-4">
              <div>
                <dt class="text-xs text-gray-500">{{ 'first_name' | translate }}</dt>
                <dd class="m-0 text-base text-gray-900">{{ employee.first_name || ('not_filled' | translate) }}</dd>
              </div>
              <div>
                <dt class="text-xs text-gray-500">{{ 'last_name' | translate }}</dt>
                <dd class="m-0 text-base text-gray-900">{{ employee.last_name || ('not_filled' | translate) }}</dd>
              </div>
              <div>
                <dt class="text-xs text-gray-500">{{ 'email' | translate }}</dt>
                <dd class="m-0 text-base">
                  @if (employee.email) {
                    <a class="text-blue-600 hover:underline" [href]="'mailto:' + employee.email">{{ employee.email }}</a>
                  } @else {
                    <span class="text-gray-400">{{ 'not_filled' | translate }}</span>
                  }
                </dd>
              </div>
              <div>
                <dt class="text-xs text-gray-500">{{ 'phone' | translate }}</dt>
                <dd class="m-0 text-base text-gray-900">{{ employee.phone || ('not_filled' | translate) }}</dd>
              </div>
              <div>
                <dt class="text-xs text-gray-500">{{ 'role' | translate }}</dt>
                <dd class="m-0 text-base text-gray-900">{{ employee.group || ('not_filled' | translate) }}</dd>
              </div>
            </dl>
          }
        </div>
      </div>
    </div>
  } @else if (employee && activeTab === EmployeeProfileTab.HISTORY) {
    <div class="crm-scroll min-h-0 flex-1 overflow-y-auto p-5">
      <app-timeline [createdBy]="employee.id" [filters]="TIMELINE_FILTERS" [showHeader]="false" [canPrefillReminders]="false" />
    </div>
  }
</section>
